import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { INDUSTRIES } from '../../data/industries';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useLanguage } from '../../contexts/LanguageContext';
import { renderT } from '../../i18n/renderT';
import type { TranslationKey } from '../../i18n';
import { langToSlug } from '../../hooks/useLocalePath';

const IndustryTabs: React.FC = () => {
    const location = useLocation();
    const [activeSlug, setActiveSlug] = React.useState<string>(INDUSTRIES[0].slug);
    const sectionRef = useRef<HTMLDivElement>(null);
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;

    useScrollReveal();

    // Handle hash-based deep-linking: /industries#retail
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        const match = INDUSTRIES.find(i => i.slug === hash);
        if (match) {
            setActiveSlug(match.slug);
            requestAnimationFrame(() => {
                const target = document.getElementById('industries-showcase');
                if (!target) return;
                // Force-reveal elements so they have layout for scrollIntoView
                target.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
                target.classList.add('visible');
                target.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }, [location.hash]);

    const activeIndustry = INDUSTRIES.find(i => i.slug === activeSlug) ?? INDUSTRIES[0];

    return (
        <div ref={sectionRef}>
            {/* ── Industries Showcase ── */}
            <section className="ind-showcase" id="industries-showcase">
                <div className="container">
                    <div className="section-label reveal">{t('ind.showcaseLabel')}</div>
                    <h2 className="ind-showcase-headline reveal">
                        {renderT(t('ind.showcaseHeadline'))}
                    </h2>
                    <p className="ind-showcase-sub reveal reveal-d1">
                        {t('ind.showcaseSub')}
                    </p>

                    {/* Tab nav — card grid */}
                    <div className="ind-nav-grid reveal reveal-d2">
                        {INDUSTRIES.map((ind) => (
                            <button
                                key={ind.slug}
                                id={ind.slug}
                                className={`ind-nav-card${activeSlug === ind.slug ? ' active' : ''}`}
                                onClick={() => setActiveSlug(ind.slug)}
                            >
                                <div className="ind-nav-icon">{ind.navIcon}</div>
                                <div className="ind-nav-text">
                                    <div className="ind-nav-name">{t(`ind.${ind.slug}.name` as TranslationKey)}</div>
                                    <div className="ind-nav-teaser">{t(`ind.${ind.slug}.teaser` as TranslationKey)}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Active panel */}
                    <div className="ind-panel active" id={`panel-${activeIndustry.slug}`} role="tabpanel">
                        {/* Panel header */}
                        <div className="ind-panel-header">
                            <div className="ind-panel-icon">{activeIndustry.panelIcon}</div>
                            <div className="ind-panel-title"><em>{t(`ind.${activeIndustry.slug}.name` as TranslationKey)}</em></div>
                            <p className="ind-panel-context">{t(`ind.${activeIndustry.slug}.context` as TranslationKey)}</p>
                        </div>

                        {/* KPI row */}
                        <div className="ind-kpis">
                            <div className="ind-kpis-label">{t('ind.kpisLabel')}</div>
                            <div className="ind-kpi-tags">
                                {Array.from({ length: activeIndustry.kpis.length }, (_, i) => (
                                    <span key={i} className="ind-kpi-tag">
                                        {t(`ind.${activeIndustry.slug}.kpi.${i + 1}` as TranslationKey)}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Use cases grid */}
                        <div className="ind-uc-label">{t('ind.useCasesLabel')}</div>
                        <div className="ind-uc-grid">
                            {activeIndustry.useCases.map((uc) => (
                                <div key={uc.num} className="ind-uc-card">
                                    <div className="ind-uc-num">{uc.num}</div>
                                    <div className="ind-uc-name">{t(`ind.${activeIndustry.slug}.uc${uc.num}.name` as TranslationKey)}</div>
                                    <div className="ind-uc-desc">{t(`ind.${activeIndustry.slug}.uc${uc.num}.desc` as TranslationKey)}</div>
                                </div>
                            ))}
                        </div>

                        {/* Panel CTA */}
                        <div className="ind-panel-cta">
                            <Link to={lp('/contact#contact-form')}>
                                {t(`ind.${activeIndustry.slug}.cta` as TranslationKey)}
                                <svg viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IndustryTabs;
